import Immutable from 'immutable';

import {
	webapi,
	createModuleState,
	EntitySearchAndDetailModel,
	DefinitionUtils,
	EntityDetailModel,
	styled,
	FormDefinition as Form,
	FormItemDefinition as FormItem,
	CrossDefinition as Cross,
	ContainerDefinition as Container,
	CentererDefinition as Centerer,
	ButtonDefinition as Button,
	Button as ButtonComponent,
	MessageDefinition as Message,
	REPORTED_EXCEPTION,
	updateEntityTimestamp,
	showMessageBox,
	createModelForState,
	DateUtils,
} from 'ps-gui';

import {
	getFormItemLabel,
	getPhrase
} from '../data/phrases';


import {
	createSearchModel, normalizePhoneNumber
} from './utils';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_STATE = createModuleState('BankId');

export default class BankIdModel {
	#data;

	constructor(data) {
		this.#data = data || EMPTY_MAP;
	}

	#setProperty(name, value) {
		if (Array.isArray(name)) {
			return new BankIdModel(this.#data.setIn(name, value));
		} else {
			return new BankIdModel(this.#data.set(name, value));
		}
	}


	#setInitialized() {
		return new BankIdModel(this.#data.set('initialized', true));
	}

	setFixedFilter(name, value) {
		const fixedFilter = this.#data.get('fixedFilter') || EMPTY_MAP;
		return this.#setProperty('fixedFilter', fixedFilter.set(name, value));
	}

	async refreshSearch() {
		const ff = this.#data.get('fixedFilter') || EMPTY_MAP;
		let fixedFilter = {
			include_matched: ff.get('matched') ? 'Y' : null,
			include_unmatched: ff.get('unmatched') ? 'Y' : null,
			include_deleted: ff.get('deleted') ? 'Y' : null,
		};

		let srch = this.getSearch().setFixedFilter(fixedFilter);
		srch = await srch.search(1);
		return this.setSearch(srch);
	}

	getSearch() {
		const esad = createModelForState(this.#data.get('esad'));
		return esad.getSearch();
	}

	setSearch(srch) {
		const esad = createModelForState(this.#data.get('esad'));
		return this.setEntitySearchAndDetail(esad.setSearch(srch));
	}

	async initialize() {
		let srch = createSearchModel('/api/dq/sv_bank_identity');


		let res = this.setEntitySearchAndDetail(
			new EntitySearchAndDetailModel()
				.setSearch(srch)
				.setDetail(getDetailModel())
				.setNewRowMode(EntitySearchAndDetailModel.NEW_ROW_MODE_NONE)
				.setEntityName('bankIdentity')
		).setFixedFilter('unmatched', true).#setInitialized();

		return await res.refreshSearch();
	}

	setEntitySearchAndDetail(model) {
		return new BankIdModel(this.#data.set('esad', model.getState()));
	}

	getData() {
		return this.#data;
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}

async function setBankIdentityUser(ctx, userId, skipConfirmation) {
	const bankIdentityId = ctx.getRootProperty(['detail', 'id']);
	if (!skipConfirmation && userId) {
		const ci = await webapi.execute('/api/proc/api_bankidentity/getConfirmationInfo', {
			bankIdentityId,
			userId
		});
		if (ci === REPORTED_EXCEPTION) {
			ctx.setModel(await ctx.getModel().reload());
			return;
		}
		let emails = ci.userEmail;
		if (ci.biEmail && ci.biEmail.toLowerCase() !== (ci.userEmail || '').toLowerCase()) {
			emails += ' ' + getPhrase('and') + ' ' + ci.biEmail;
		}
		let sb = [];
		sb.push('\n');
		sb.push(ci.userDisplayName + ', ' + ci.userEmail + ', ' + ci.userPhone);
		if (ci.parties) {
			sb.push('\n');
			ci.parties.forEach(p => {
				sb.push('\n');
				sb.push(p.partyId + ' - ' + p.name);
				if (p.dateOfBirth) {
					sb.push(' (');
					sb.push(DateUtils.formatDate(p.dateOfBirth));
					sb.push(')');
				}
			});
		}
		const userInfo = sb.join('');
		const dlgRes = await showMessageBox(getPhrase('setBankIdentityUserConfirmation').replace('$userName', ci.userName).replace('$emails', emails).replace('$userInfo', userInfo), {
			type: 'confirm'
		});
		if (dlgRes !== 'yes') {
			ctx.setModel(await ctx.getModel().reload());
			return;
		}
	}
	const res = await webapi.execute('/api/bi/setBankIdentityUser', {
		id: bankIdentityId,
		userId
	});
	if (res === REPORTED_EXCEPTION) {
		return;
	}
	updateEntityTimestamp('bankIdentity');
	ctx.setModel(await ctx.getModel().reload());
}

let detailDefinition = Centerer(Container([Form().setOpacity(ctx => ctx.getProperty('isDeleted') === 'Y' ? '0.3' : '1').setVirtual(false).setBasePath('detail').setItems([
	Cross().setItems([
		FormItem('name', 'text').setReadOnly(true),
		FormItem('birthDate', 'date').setReadOnly(true),
		FormItem('birthNumber', 'text').setReadOnly(true),
	]),
	Cross().setItems([
		FormItem('phoneNumber', 'text').setReadOnly(true),
		FormItem('email', 'text').setReadOnly(true),
		FormItem('primaryNationality', 'text').setReadOnly(true),
	]),
	Cross().setItems([
		FormItem('fromDate', 'date').setReadOnly(true).setWithTime(true).setLabel(ctx => getFormItemLabel('createdWhen')),
		FormItem('userMatchedWhen', 'date').setReadOnly(true).setWithTime(true),
		FormItem('toDate', 'date').setReadOnly(true).setWithTime(true).setLabel(ctx => getFormItemLabel('deletedWhen')),
	]),
	Button('delete', getPhrase('delete')).setListener(async e => {
		const ctx = e.ctx;
		const dlgRes = await showMessageBox(getPhrase('reallyDeleteBankIdentity'), { type: 'confirm' });
		if (dlgRes !== 'yes') {
			return;
		}
		const res = await webapi.execute('/api/proc/api_bankidentity/deleteBankIdentity', {
			id: ctx.getProperty('id')
		});
		if (res === REPORTED_EXCEPTION) {
			return;
		}
		updateEntityTimestamp('bankIdentity');
		ctx.setModel(await ctx.getModel().reload());
	}).setVisible(ctx => !ctx.getProperty('toDate')),
	Cross().setItems([
		FormItem('userId', 'lookup')
			.setLookup('nativeUser')
			.setLabel(ctx => getFormItemLabel('matchedUser'))
			.setReadOnly(ctx => ctx.getProperty('toDate'))
			.onChange((ctx, item, value) => {
				setBankIdentityUser(ctx, value);
			}),
	]),
	Message(ctx => {
		if (ctx.getProperty('userId') || ctx.getProperty('toDate')) {
			return null;
		}
		const candidates = ctx.getProperty('candidates');
		if (!candidates || candidates.size === 0) {
			return null;
		}
		return (
			<Candidates>
				<div className='header'>{getPhrase('candidates')}</div>
				{
					candidates.map((candidate, index) => {
						return (
							<Candidate key={index}>
								{
									candidateAttributes.map((attr, index) => {
										return (
											<span className='attribute' key={index}>
												<span className='label'>{getFormItemLabel(attr)}:</span>
												<span className='value'>{candidate.get(attr)}</span>
											</span>
										);
									})
								}
								<div className='buttons'>
									<ButtonComponent
										text={getPhrase('matchUser')}
										type="light"
										onAction={() => {
											setBankIdentityUser(ctx, candidate.get('userId'));
										}}
									/>
								</div>
							</Candidate>
						);
					})
				}
			</Candidates>
		);
	}),
	Button('createUser', getPhrase('createUser')).setListener(e => {
		const ctx = e.ctx;
		let newUser = EMPTY_MAP
			.set('displayName', ctx.getProperty('name'))
			.set('phone', ctx.getProperty('phoneNumber'))
			.set('email', ctx.getProperty('email'))
			.set('userName', ctx.getProperty('email'));
		ctx.setProperty('newUser', newUser);
	}).setVisible(ctx => !ctx.getProperty('userId') && !ctx.getProperty('newUser') && !ctx.getProperty('toDate')),
]),
Container([
	Message(ctx => (<h1>{getPhrase('newUser')}</h1>)),
	Form().setBasePath(['detail', 'newUser']).setVirtual(false).setItems([
		Cross().setItems([
			FormItem('userName', 'text')
				.setMaxLength(200)
				.setReadOnly(ctx => ctx.getProperty('id'))
				.onChange((ctx, me, value) => {
					if (value && value.indexOf('@') > 0) {
						ctx.setProperty('email', value);
					}
				}),
			FormItem('displayName', 'text')
		]),
		Cross().setItems([
			FormItem('phone', 'text')
				.onChange((ctx, me, value) => {
					const normalized = normalizePhoneNumber(value);
					if (normalized && normalized !== value) {
						ctx.setProperty('phone', normalized);
					}
				}),
			FormItem('email', 'text'),
			FormItem('languageId', 'lookup').setLookup('language')
		]),
		Cross().setItems([FormItem('comments', 'text').setMultiline(true)]),
		Cross().setItems([FormItem('parties', 'lookupList').setLookup('partyIdName')]),
		Button('createAndMatch', getPhrase('createAndMatch')).setListener(async e => {
			const ctx = e.ctx;
			const res = await webapi.execute('/api/proc/api_user/saveUser', ctx.getData().toJS());
			if (res === REPORTED_EXCEPTION) {
				return;
			}
			setBankIdentityUser(ctx, res.id, true);
			updateEntityTimestamp('bankIdentity');
			updateEntityTimestamp('user');
			ctx.setModel(await ctx.getModel().reload());
		}).setVisible(ctx => !ctx.getProperty('toDate')),
	])
]).setVisible(ctx => ctx.getProperty(['detail', 'newUser']))
])).setWidth('96%').setMaxWidth('1000px');

const candidateAttributes = [
	'userName',
	'displayName',
	'email',
	'phone'
];

const Candidate = styled.div`
	text-align: left;
	padding: 16px;
	border-radius: 8px;

	&:hover {
		background: ${props => props.theme.tableRowHoverBackground};
	}

	.label {
		display: inline-block;
		color: ${props => props.theme.disabledTextColor};
		padding: 4px;
		font-size: 16px;
		font-weight: 700;
	}

	.value {
		display: inline-block;
		color: ${props => props.theme.disabledTextColor};
		padding: 4px;
		font-size: 16px;
		font-weight: 400;
		margin-right: 16px;
	}

	.buttons {
		text-align: right;
	}

`;

const Candidates = styled.div`
	margin-top: 16px;
	margin-bottom: 16px;

	padding: 8px;
	padding: ${props => props.theme.inputPadding};
	border-radius: ${props => props.theme.inputBorderRadius};
	border: ${props => props.theme.inputBorder};
	background: ${props => props.theme.inputBackground};

	text-align: center;

	.header {
		color: ${props => props.theme.disabledTextColor};
		padding: 4px;
		font-size: 16px;
		font-weight: 700;
		margin-bottom: 8px;
		text-align: center;
	}

`;


const ActivationMessage = styled.div`
	color: ${props => props.theme.lightButtonColor};
	font-weight: 500;

	margin: 8px;
	text-align: right;
`;

let detailModel = null;

export function getDetailModel() {
	if (!detailModel) {
		DefinitionUtils.setLabels(detailDefinition);

		detailModel = new EntityDetailModel()
			.setLoadUri('/api/proc/api_bankidentity/getBankIdentityDetail')
			.setDefinition(detailDefinition)
			.setEntityName('bankIdentity');
	}
	return detailModel;
}

